/**
 * The message users see while the system is being updated — and when it is back.
 *
 * Desk, 2026-09-27: "users should see the info that the page is working, kindly
 * come back after 5 minutes… users lose actions while the backend is doing
 * something and think the application is bad."
 *
 * It says three things a stranded user needs and a raw error never does:
 *   1. it is the SYSTEM, not them or their computer;
 *   2. how long to wait, and that saved work is safe;
 *   3. do not repeat the last action blindly — look the record up first. A
 *      payment or bill pressed twice while nothing answered is the real damage.
 *
 * Blocking, because a clerk left free to keep clicking on a screen that cannot
 * save is how work is lost. It cannot be dismissed while the outage lasts; it
 * clears itself when the server answers, then asks for one "Noted" so the user
 * knows to check their last action.
 */
import { useEffect, useState, type JSX } from 'react';
import dayjs from 'dayjs';
import { Button, Dialog, Icon } from '@components/ui-tw';
import { checkNow, watchForNewVersion } from '@core/api/backend-availability';
import { useSystemStatusStore } from '@store/system-status.store';

export function SystemStatusNotice(): JSX.Element {
  const phase = useSystemStatusStore((s) => s.phase);
  const since = useSystemStatusStore((s) => s.since);
  const lastCheckedAt = useSystemStatusStore((s) => s.lastCheckedAt);
  const dismiss = useSystemStatusStore((s) => s.dismiss);
  const [checking, setChecking] = useState(false);

  useEffect(() => watchForNewVersion(), []);

  const onCheck = async (): Promise<void> => {
    setChecking(true);
    try {
      await checkNow();
    } finally {
      setChecking(false);
    }
  };

  if (phase === 'reload') {
    return (
      <Dialog
        open
        onClose={dismiss}
        dismissible={false}
        position="top"
        title="A new version has been installed"
        footer={
          <>
            <Button variant="outline" onClick={dismiss}>
              Not yet
            </Button>
            <Button autoFocus onClick={() => window.location.reload()}>
              Reload now
            </Button>
          </>
        }
      >
        <div role="alert" className="tw:flex tw:flex-col tw:gap-2 tw:text-base">
          <p className="tw:m-0">The system was updated while this screen was open, so the next page cannot load until this one is refreshed.</p>
          <p className="tw:m-0 tw:text-sm tw:text-nx-text-muted">
            If you are in the middle of typing something, choose <b>Not yet</b>, finish and save it, then refresh.
          </p>
        </div>
      </Dialog>
    );
  }

  if (phase === 'recovered') {
    return (
      <Dialog
        open
        onClose={dismiss}
        dismissible={false}
        position="top"
        title="The system is back"
        footer={
          <Button autoFocus onClick={dismiss}>
            Noted
          </Button>
        }
      >
        <div role="alert" className="tw:flex tw:flex-col tw:gap-3 tw:text-base">
          <div className="tw:flex tw:items-start tw:gap-3">
            <span className="tw:flex tw:size-10 tw:shrink-0 tw:items-center tw:justify-center tw:rounded-full tw:bg-nx-green-tint tw:text-nx-green-on-tint">
              <Icon name="check_circle" size={24} />
            </span>
            <p className="tw:m-0">The update is finished and everything is working again. Thank you for waiting.</p>
          </div>
          <div className="tw:rounded-md tw:bg-nx-yellow-tint tw:px-3 tw:py-2 tw:text-sm tw:text-nx-yellow-on-tint">
            <b>Before you continue: </b>
            if you were saving or paying for something when the screen stopped, look that record up first to see whether it went through. Only repeat it if it did not.
          </div>
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={phase === 'updating'}
      onClose={() => undefined}
      dismissible={false}
      position="top"
      title="The system is being updated"
      footer={
        <Button variant="outline" loading={checking} onClick={() => void onCheck()}>
          Check now
        </Button>
      }
    >
      {phase === 'updating' && (
        <div role="alert" className="tw:flex tw:flex-col tw:gap-3 tw:text-base">
          <div className="tw:flex tw:items-start tw:gap-3">
            <span className="tw:flex tw:size-10 tw:shrink-0 tw:items-center tw:justify-center tw:rounded-full tw:bg-nx-blue-tint tw:text-nx-blue-on-tint">
              <Icon name="info" size={24} />
            </span>
            <div className="tw:flex tw:flex-col tw:gap-1">
              <p className="tw:m-0">
                The system is being updated, so this page cannot save or load right now. <b>It is not a problem with your computer or with what you did.</b>
              </p>
              <p className="tw:m-0">
                Please wait about <b>5 minutes</b> and then come back to this screen. This message will change by itself when the system is ready.
              </p>
            </div>
          </div>
          <ul className="tw:m-0 tw:flex tw:list-disc tw:flex-col tw:gap-1 tw:pl-9 tw:text-sm">
            <li>Everything that was already saved is safe.</li>
            <li>Please do not keep pressing the same button — it will not save until the system is back.</li>
            <li>If you were saving or paying for something, look the record up afterwards before you repeat it.</li>
          </ul>
          <p className="tw:m-0 tw:text-label tw:text-nx-text-muted">
            {since ? `Started ${dayjs(since).format('HH:mm:ss')}` : ''}
            {lastCheckedAt ? ` · last checked ${dayjs(lastCheckedAt).format('HH:mm:ss')} — still updating` : ' · checking every few seconds'}
          </p>
        </div>
      )}
    </Dialog>
  );
}
